// Disabled operator fixture. Never captures devices, obtains tokens, joins RTC or starts AI.
const preparations = new WeakMap();
export async function prepareFixedInput(room, deps = {}) {
  if (preparations.has(room) || room.state !== 'connected' || room.localParticipant.trackPublications.size !== 0)
    throw new Error('Use one connected owner preparation with devices off.');
  const lease = {}; preparations.set(room, lease);
  const now = deps.now || (() => performance.now());
  const preparedAt = now(), preparationDeadline = preparedAt + 60000;
  const absoluteDeadline = preparedAt + 120000;
  let context, destination, source, publication, rawTrack, publishing, timer;
  let closed = false, played = false, closing, disposing, signalStopped;
  const stopped = new Promise(resolve => { signalStopped = resolve; });
  const active = promise => Promise.race([promise, stopped.then(() => { throw new Error('Fixture closed.'); })]);
  const failures = [];
  const attempt = async (label, action) => {
    let timeout;
    try {
      await Promise.race([Promise.resolve().then(action), new Promise((_, reject) => {
        timeout = setTimeout(() => reject(new Error('Cleanup timeout')), 2000);
      })]);
    } catch (_) { failures.push(label); }
    finally { clearTimeout(timeout); }
  };
  const dispose = p => {
    if (!disposing) disposing = Promise.resolve().then(() => room.localParticipant.unpublishTrack(p.track));
    return disposing;
  };
  const owns = p => !!p && !!rawTrack && (p === publication || p.track === rawTrack || p.track?.mediaStreamTrack === rawTrack);
  const ownedOnly = () => {
    const pubs = [...room.localParticipant.trackPublications.values()];
    return room.state === 'connected' && pubs.length === 1 && owns(pubs[0]);
  };
  function close() {
    if (closing) return closing;
    closed = true; clearTimeout(timer); signalStopped();
    // Stop our generated source synchronously, even if every SDK promise hangs.
    try { rawTrack?.stop(); } catch (_) { failures.push('generated_track_stop'); }
    closing = Promise.resolve().then(async () => {
      // Start independently: one hung operation cannot delay any other cleanup.
      await Promise.all([
        attempt('source_stop', () => source?.stop()),
        attempt('source_disconnect', () => source?.disconnect()),
        attempt('destination_disconnect', () => destination?.disconnect()),
        attempt('context_close', () => context?.close()),
        ...listeners.map(([event, fn]) => attempt('listener_remove', () => room.off(event, fn))),
        attempt('abort_listener_remove', () => deps.signal?.removeEventListener('abort', safeClose)),
        attempt('unpublish', async () => {
          const p = publication || (publishing ? await publishing : null);
          if (p) await dispose(p);
        }),
      ]);
      if (!failures.length && preparations.get(room) === lease) preparations.delete(room);
      return { cleaned: failures.length === 0, failures: [...failures] };
    });
    return closing;
  }
  const safeClose = () => close().catch(() => ({ cleaned: false, failures: ['cleanup_failed'] }));
  const changed = p => { if (!owns(p)) safeClose(); };
  const listeners = [['disconnected', safeClose], ['localTrackPublished', changed]];
  try {
    for (const [event, fn] of listeners) room.on(event, fn);
    deps.signal?.addEventListener('abort', safeClose, { once: true });
    if (deps.signal?.aborted) throw new Error('Preparation cancelled.');
    context = new (deps.Context || window.AudioContext)();
    timer = setTimeout(safeClose, Math.max(0, preparationDeadline - now()));
    await active(context.resume());
    const response = await active((deps.fetch || window.fetch.bind(window))('/diagnostics/fixed-speech.wav', { signal: deps.signal }));
    if (!response.ok || closed) throw new Error('Fixed fixture unavailable or cancelled.');
    const buffer = await active(context.decodeAudioData(await active(response.arrayBuffer())));
    if (closed || room.state !== 'connected' || room.localParticipant.trackPublications.size !== 0 ||
        buffer.numberOfChannels !== 1 || !(buffer.duration > 0 && buffer.duration <= 12))
      throw new Error('Fixture cancelled or room publications changed.');
    destination = context.createMediaStreamDestination(); rawTrack = destination.stream.getAudioTracks()[0];
    publishing = Promise.resolve(room.localParticipant.publishTrack(rawTrack,
      { source: 'microphone', name: 'Explicit fixed diagnostic speech' })).then(p => {
        publication = p;
        // Installed before waiting: disposal survives main cleanup timeout.
        if (closed) void attempt('late_unpublish', () => dispose(p));
        return p;
      });
    await active(publishing);
    if (closed || !ownedOnly()) throw new Error('Publication changed or preparation cancelled.');
    return { close, play() {
      if (closed || played || now() >= preparationDeadline || !ownedOnly()) { safeClose(); throw new Error('Fixture unavailable or already used.'); }
      played = true;
      try {
        // Preparation must not shorten an already bounded backend session.
        // This one-time generated-track watchdog never renews paid usage.
        clearTimeout(timer);
        timer = setTimeout(safeClose, Math.max(0, Math.min(60000, absoluteDeadline - now())));
        source = context.createBufferSource(); source.buffer = buffer;
        source.connect(destination); source.start();
      } catch (error) { safeClose(); throw error; }
    }};
  } catch (error) { await close(); throw error; }
}
